import { memo, useRef, useCallback, useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Alert,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQueryBuilder, useSaveAndCountQuery } from '../../../../hooks/QueryBuilder';
import { useAvailableGroups } from '../../../../hooks/QueryBuilder';
import QueryRuleGroup from '../../../Clients/QueryBuilder/components/QueryRuleGroup';
import { GroupSelector } from '../../../Clients/QueryBuilder/components/GroupSelector';
import { toast } from 'react-toastify';

export interface TargetingFormData {
  title: string;
  description: string;
  queryId: number | null;
  queryDescription: string;
  nbRecipients: number | null;
}

interface CommunicationTargetingStepProps {
  data: TargetingFormData;
  onChange: (patch: Partial<TargetingFormData>) => void;
}

function CommunicationTargetingStep({ data, onChange }: CommunicationTargetingStepProps) {
  const {
    groups: ruleGroups,
    categories,
    isValid,
    addRule,
    removeRule,
    updateRule,
    addGroup,
    removeGroup,
    resetGroups,
    fields,
    loadingFields,
  } = useQueryBuilder();

  const { groups: availableGroups, loading: loadingGroups, refetch: refetchGroups } = useAvailableGroups();
  const { saveAndCountQuery, loading: executing } = useSaveAndCountQuery();

  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [descResetKey, setDescResetKey] = useState(0);
  const queryDescRef = useRef(data.queryDescription);

  useEffect(() => {
    queryDescRef.current = data.queryDescription;
  }, [data.queryDescription]);

  const handleGroupChange = (groupId: number | null, groupName?: string) => {
    setSelectedGroupId(groupId);
    if (groupName) setNewGroupName('');
  };

  const handleCreateGroup = (groupName: string) => {
    setNewGroupName(groupName);
    setSelectedGroupId(-1);
  };

  const handleReset = () => {
    resetGroups();
    queryDescRef.current = '';
    onChange({ queryDescription: '', queryId: null, nbRecipients: null });
    setDescResetKey(k => k + 1);
    setSelectedGroupId(null);
  };

  const handleExecute = useCallback(async () => {
    const desc = queryDescRef.current.trim();
    if (!isValid) return toast.warning('Veuillez compléter toutes les règles');
    if (!desc) return toast.warning('Veuillez renseigner un nom de requête');

    const toastId = toast.loading('Exécution de la requête en cours...');
    try {
      const result = await saveAndCountQuery({
        description: desc,
        groups: ruleGroups,
        groupId: selectedGroupId && selectedGroupId > 0 ? selectedGroupId : undefined,
      });

      if (!result) {
        toast.update(toastId, { render: "Erreur lors de l'exécution", type: 'error', isLoading: false, autoClose: 5000 });
        return;
      }

      if (result.mode === 'sync') {
        onChange({ queryId: result.queryId, nbRecipients: result.count, queryDescription: desc });
        toast.update(toastId, {
          render: `Requête exécutée : ${result.count.toLocaleString('fr-FR')} destinataire(s)`,
          type: 'success',
          isLoading: false,
          autoClose: 5000,
        });
      } else {
        onChange({ queryId: result.queryId ?? null, queryDescription: desc });
        toast.update(toastId, { render: 'Requête lancée en arrière-plan', type: 'info', isLoading: false, autoClose: 5000 });
      }
    } catch {
      toast.update(toastId, { render: "Erreur lors de l'exécution", type: 'error', isLoading: false, autoClose: 5000 });
    }
  }, [isValid, ruleGroups, selectedGroupId, saveAndCountQuery, onChange]);

  const hasRules = ruleGroups.some(g => g.rules.length > 0);

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
        Requête de ciblage
      </Typography>

      {data.queryId !== null && (
        <Alert
          icon={<CheckCircleIcon fontSize="small" />}
          severity="success"
          sx={{ mb: 2, fontSize: '0.8rem' }}
          action={
            <Button size="small" color="inherit" onClick={handleReset}>
              Modifier
            </Button>
          }
        >
          Requête #{data.queryId} — <strong>{data.queryDescription}</strong>
          {data.nbRecipients !== null && (
            <span> — {data.nbRecipients.toLocaleString('fr-FR')} destinataire(s)</span>
          )}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <TextField
            key={descResetKey}
            size="small"
            label="Nom de la requête *"
            defaultValue={data.queryDescription}
            onChange={e => { queryDescRef.current = e.target.value; }}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <GroupSelector
            selectedGroupId={selectedGroupId}
            newGroupName={newGroupName}
            availableGroups={availableGroups.map(g => ({ id: g.id, description: g.description }))}
            loading={loadingGroups}
            onGroupIdChange={handleGroupChange}
            onCreateGroup={handleCreateGroup}
            onRefetchGroups={refetchGroups}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              sx={{ textTransform: 'none', borderColor: '#6c757d', color: '#6c757d' }}
            >
              Réinit.
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={executing ? <CircularProgress size={12} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleExecute}
              disabled={executing || !isValid || !hasRules}
              sx={{ textTransform: 'none', bgcolor: '#556ee6', '&:hover': { bgcolor: '#4558c8' } }}
            >
              Exécuter
            </Button>
          </Box>
        </Box>

        {loadingFields ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {ruleGroups.map((group, index) => (
              <Box key={group.id}>
                <QueryRuleGroup
                  group={group}
                  categories={categories}
                  fields={fields}
                  onAddRule={() => addRule(group.id)}
                  onUpdateRule={(ruleId, ruleData) => updateRule(group.id, ruleId, ruleData)}
                  onRemoveRule={(ruleId) => removeRule(group.id, ruleId)}
                  onRemoveGroup={() => removeGroup(group.id)}
                  showCombinator={index > 0}
                  canRemoveGroup={ruleGroups.length > 1}
                />
                {index < ruleGroups.length - 1 && (
                  <Box sx={{ textAlign: 'center', my: 2 }}>
                    <Chip label="OU" sx={{ bgcolor: '#ffc107', color: 'white', fontWeight: 600 }} />
                  </Box>
                )}
              </Box>
            ))}

            {ruleGroups.length < 2 && (
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addGroup}
                sx={{ mt: 1, textTransform: 'none', borderColor: '#556ee6', color: '#556ee6', borderStyle: 'dashed' }}
              >
                Ajouter un groupe (OU)
              </Button>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}

export default memo(CommunicationTargetingStep);
